import { map, catchError } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';
import { Injectable, Optional, Inject } from '@angular/core';
import {
  Http,
  Request,
  Response,
  RequestMethod,
  RequestOptions,
  BaseRequestOptions,
  URLSearchParams,
  Headers,
} from '@angular/http';
import { Cookie } from './cookie';
import { AuthService } from './auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { AmToasterService } from '@angularminds/am-toaster';

/**
 * This class provides the NameList service with methods to read names and add names.
 */
@Injectable()
export class RestService {
  restOptions: RequestOptions;
  baseApi = '';
  /**
   * Creates a new NameListService with the injected Http.
   */
  constructor(
    private http: Http,
    private authService: AuthService,
    private httpClient: HttpClient,
    private amToasterService: AmToasterService,
    private router: Router,
  ) {
    this.restOptions = new BaseRequestOptions();
    this.restOptions.headers.append('Content-Type', 'application/json');
  }

  /**
   * Returns an Observable for the HTTP GET request for the JSON resource.
   */
  fetch(path: string, search?: Object, absoluteUrl?: boolean): Observable<any> {
    return this.request(path, RequestMethod.Get, undefined, search, absoluteUrl);
  }

  /**
   * Returns an Observable for the HTTP POST request for the JSON resource.
   */
  post(path: string, body: Object, absoluteUrl?: boolean): Observable<any> {
    return this.request(path, RequestMethod.Post, body, undefined, absoluteUrl);
  }

  /**
   * Returns an Observable for the HTTP PUT request for the JSON resource.
   */
  put(path: string, body: Object, absoluteUrl?: boolean): Observable<any> {
    return this.request(path, RequestMethod.Put, body, undefined, absoluteUrl);
  }

  /**
   * Returns an Observable for the HTTP DELETE request for the JSON resource.
   */
  delete(path: string, absoluteUrl?: boolean): Observable<any> {
    return this.request(path, RequestMethod.Delete, undefined, undefined, absoluteUrl);
  }

  /**
   * Returns an Observable for the HTTP FILE POST request for the JSON resource.
   */
  postFile(path: string, body: Object, absoluteUrl?: boolean): Observable<any> {
    return this.request(path, RequestMethod.Post, body, undefined, absoluteUrl, true);
  }

  postFileWithProgress(path: string, body: Object, options: any): Observable<any> {
    const headers = new HttpHeaders();
    const _token = Cookie.get('_token');
    if (_token) {
      headers.set('Authorization', `${_token}`);
    }
    options['headers'] = headers;
    return this.httpClient.post(path, body, options);
  }
  /**
   * Serialize search object to params
   */
  serializeSearch(obj: any): URLSearchParams {
    const params = new URLSearchParams();
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        params.set(encodeURIComponent(p), encodeURIComponent(obj[p]));
      }
    }
    return params;
  }

  getRequestUrl(path: string): string {
    return `${window.location.protocol}//${window.location.host}` + (path ? path : ``);
  }

  getParentDomainRequestUrl(path: string): string {
    const host =
      window.location.hostname === 'localhost' ? window.location.host : `${this.getHost().host}`;
    return `${window.location.protocol}//${host}` + (path ? path : ``);
  }

  getHost() {
    if (window.location.hostname !== 'localhost') {
      const rawHost = window.location.host.split('.');
      const hosts: any = [];
      const nonHosts: any = [];
      rawHost.forEach((innerHost: any, index: number) => {
        if (index >= rawHost.length - 2) {
          hosts.push(innerHost);
        } else if (index === rawHost.length - 3) {
          hosts.push(innerHost);
        } else {
          nonHosts.push(innerHost);
        }
      });
      return {
        host: hosts.join('.'),
        nonHost: nonHosts.join('.'),
      };
    } else {
      return {
        host: window.location.host,
        nonHost: '',
      };
    }
  }

  /**
   * Actual rest request based on params
   */
  private request(
    path: string,
    method: RequestMethod,
    body?: Object,
    search?: Object,
    absoluteUrl?: boolean,
    isFile?: boolean,
  ): Observable<any> {
    const url = absoluteUrl ? path : this.getRequestUrl(path);
    const headers = new Headers();
    if (!isFile) {
      headers.set('Content-Type', 'application/json');
    }
    const options = new RequestOptions(
      this.restOptions.merge({
        method: method,
        url: url,
        body: !isFile ? JSON.stringify(body) : body,
        search: this.serializeSearch(search),
        headers: headers,
      }),
    );

    const _token = Cookie.get('_token');
    if (_token) {
      options.headers.set('Authorization', `${_token}`);
    }

    return this.http.request(new Request(options)).pipe(
      map((res: Response) => {
        // because 204 does not contain body
        if (res.status >= 201 && res.status <= 226) {
          return;
        }
        {
          return res.json();
        }
      }),
      catchError((err: any) => this.handleError(err)),
    );
  }

  logout(): Observable<any> {
    return new Observable<any>((observer: any) => {
      // clear token remove user from local storage to log user out
      this.removeAuthCookie();
      observer.next();
    });
  }

  removeAuthCookie() {
    const host = window.location.hostname === 'localhost' ? '' : `.${this.getHost().host}`;
    localStorage.removeItem('_activeUser');
    Cookie.delete('_token', '/', host);
    Cookie.delete('_google_token', '/', host);
    Cookie.delete('_host', '/', host);
    Cookie.deleteAll('/', host || 'localhost');
  }

  /**
   * Handle HTTP error
   */
  handleError(error: any) {
    // We'd also dig deeper into the error to get a better message
    let errMsg = error.status
      ? `${error.status} - ${error.statusText || error.message}`
      : 'Server error';
    if (error._body) {
      try {
        const json = error.json();
        if (json.message) {
          errMsg = json.message;
        } else {
          errMsg = json;
        }
      } catch (error) {}
    }
    if (error.status === 401) {
      this.amToasterService.show('error', 'Your session has been expired!', 'Oops!');
      this.authService.localLogout().subscribe(logout => {
        this.router.navigate(['/auth/login']);
        // window.location.href = this.getRequestUrl('/auth/login');
      });
    } else if (error.status === 403) {
      this.amToasterService.show('error', errMsg || 'Internal error!', 'Oops!');
      window.location.href = this.getRequestUrl('/dashboard');
    }
    // else {
    //   this.amToasterService.show('error', errMsg || 'Internal error!', 'Oops!');
    // }
    return throwError(errMsg);
  }
}
